import React, { useState, useEffect } from 'react';

import * as MUI from '@mui/material';

import { CloseIcon } from './Styles/Icons';
import {
	InputGroup,
	TextArea,
	ButtonGroup,
	Button,
	FlexRow,
	FlexColumn,
	Dialog,
	DialogActions,
	DialogTitle,
	DialogContent,
	CircularButton,
	LocalizationProvider,
	DatePicker,
	RadioGroup,
	ResolvedDateContainer,
	ResolvedDate,
	ResolvedDateNote,
	AddCommentLink,
} from './Styles/BackorderDialogBox';

import { BackorderPresets, COMMENT_MAX_LENGTH, formatShipByDate } from './Utils';
import { TextField } from '../Styles';

const addDays = (date, days) => {
	const result = new Date(date);
	result.setDate(result.getDate() + days);
	return result;
};

const startOfDay = (date) => {
	const result = new Date(date);
	result.setHours(0, 0, 0, 0);
	return result;
};

const PRESET_OPTIONS = [
	{ value: BackorderPresets.ONE_WEEK, name: 'In 1 week' },
	{ value: BackorderPresets.TWO_WEEKS, name: 'In 2 weeks' },
	{ value: BackorderPresets.THIRTY_DAYS, name: 'In 30 days' },
	{ value: BackorderPresets.CUSTOM, name: 'Pick a date' },
];

const BackorderDialogBox = ({ open, onTrue, onFalse, initialComment = '', initialExpectedShipDate = null, isUpdate = false }) => {
	const [preset, setPreset] = useState(null);
	const [expectedShipDate, setExpectedShipDate] = useState(null);
	const [comment, setComment] = useState('');
	const [showComment, setShowComment] = useState(false);
	const [datePickerOpen, setDatePickerOpen] = useState(false);

	const tomorrow = addDays(startOfDay(new Date()), 1);

	const onPresetSelect = (selectedPreset) => {
		setPreset(selectedPreset);

		switch (selectedPreset) {
			case BackorderPresets.ONE_WEEK:
				setExpectedShipDate(addDays(startOfDay(new Date()), 7));
				break;
			case BackorderPresets.TWO_WEEKS:
				setExpectedShipDate(addDays(startOfDay(new Date()), 14));
				break;
			case BackorderPresets.THIRTY_DAYS:
				setExpectedShipDate(addDays(startOfDay(new Date()), 30));
				break;
			case BackorderPresets.CUSTOM:
				setExpectedShipDate(null);
				break;
			default:
				break;
		}
	};

	const onTrueHandler = () => {
		if (!expectedShipDate) return;

		onTrue({ expectedShipDate, comment });
	};

	const onFalseHandler = () => {
		onFalse();
	};

	const onDialogClose = () => {
		if (datePickerOpen) {
			setDatePickerOpen(false);
			return;
		}

		onFalseHandler();
	};

	useEffect(() => {
		if (open) {
			setComment(initialComment || '');
			setShowComment(Boolean(initialComment));
			setExpectedShipDate(initialExpectedShipDate ? new Date(initialExpectedShipDate) : null);
			setPreset(initialExpectedShipDate ? BackorderPresets.CUSTOM : null);
			setDatePickerOpen(false);
		}
	}, [open]);

	return (
		<Dialog
			open={open}
			onClose={() => onDialogClose()}
			fullWidth
		>
			<DialogTitle>
				{isUpdate ? 'Update ETA' : 'Mark Item as Backordered'}
				<ButtonGroup alignRight>
					<CircularButton
						error
						outline
						onClick={() => onFalseHandler()}
					>
						<CloseIcon />
					</CircularButton>
				</ButtonGroup>
			</DialogTitle>

			<DialogContent>
				<FlexColumn>
					<InputGroup
						label='Expected Ship Date'
						markRequired={true}
					>
						<RadioGroup
							name={'expected_ship_date_preset'}
							disabled={false}
							value={preset}
							onClick={(e) => {
								onPresetSelect(e.target.value);
							}}
							options={PRESET_OPTIONS}
						/>
					</InputGroup>

					{preset === BackorderPresets.CUSTOM ? (
						<InputGroup
							label='Pick a date'
							markRequired={true}
						>
							<LocalizationProvider>
								<DatePicker
									value={expectedShipDate}
									minDate={tomorrow}
									open={datePickerOpen}
									disableCloseOnSelect={false}
									disableHighlightToday={true}
									onChange={(date) => setExpectedShipDate(date)}
									onClose={() => setDatePickerOpen(false)}
									renderInput={(params) => (
										<TextField
											ref={params.ref}
											inputRef={params.inputRef}
											placeholder='Date'
											value={expectedShipDate ? new Date(expectedShipDate).toLocaleDateString() : ''}
											onClick={() => setDatePickerOpen(true)}
											inputProps={{ ...params.inputProps, readOnly: true }}
										/>
									)}
									PopperProps={{ placement: 'bottom-start', style: { zIndex: 1400 } }}
								/>
							</LocalizationProvider>
						</InputGroup>
					) : null}

					{expectedShipDate ? (
						<ResolvedDateContainer>
							<ResolvedDate>Ships by {formatShipByDate(expectedShipDate)}</ResolvedDate>
							<ResolvedDateNote>Your customer will see this date.</ResolvedDateNote>
						</ResolvedDateContainer>
					) : null}

					{!showComment ? (
						<AddCommentLink onClick={() => setShowComment(true)}>+ Add a comment</AddCommentLink>
					) : (
						<InputGroup
							label='Comment'
							maxLength={COMMENT_MAX_LENGTH}
							inputLength={comment.length}
							message='This comment will be shown to the customer.'
						>
							<TextArea
								name='backorder_comment'
								placeholder='Enter a comment'
								value={comment}
								inputProps={{ maxLength: COMMENT_MAX_LENGTH }}
								onChange={(e) => setComment(e.target.value)}
							/>
						</InputGroup>
					)}
				</FlexColumn>
			</DialogContent>

			<DialogActions style={{ margin: '0 0.5rem' }}>
				<ButtonGroup alignRight>
					<Button
						primary
						outline
						onClick={(e) => onFalseHandler()}
					>
						Cancel
					</Button>
					<Button
						primary
						disabled={!expectedShipDate}
						onClick={(e) => onTrueHandler()}
					>
						Confirm
					</Button>
				</ButtonGroup>
			</DialogActions>
		</Dialog>
	);
};

export default BackorderDialogBox;
